import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { useOverlay } from "@base/model/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDownToLine } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { downloadImagesApi } from "../model/api";
import {
  downloadImagesQueryKeys,
  useCancelImageZipJobMutation,
  useCreateImageZipJobMutation,
  useImageZipFileQuery,
  useImageZipJobQuery,
} from "../model/queries";
import { getImageZipProcessRateNumber } from "../model/schemas";
import { DownloadImagesConfirmModal } from "./DownloadImagesConfirmModal";
import { DownloadProgressDialog } from "./DownloadProgressDialog";

const triggerDownload = (url: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

interface DownloadProgressFlowProps {
  isOpen: boolean;
  close: () => void;
  projectId: string;
  dailyLogId: string;
  initialJobId: string;
  imageCount: number;
}

const COMPLETION_DISPLAY_MS = 600;
const POLLING_TIMEOUT_MS = 5 * 60 * 1000;

function DownloadProgressFlow({
  isOpen,
  close,
  projectId,
  dailyLogId,
  initialJobId,
  imageCount,
}: DownloadProgressFlowProps) {
  const downloadProperties = useMemo(
    () => ({ project_id: projectId, log_id: dailyLogId, image_count: imageCount }),
    [projectId, dailyLogId, imageCount],
  );
  const [dialogStatus, setDialogStatus] = useState<"progress" | "success" | "error">("progress");
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [jobId, setJobId] = useState(initialJobId);
  const [isCancelling, setIsCancelling] = useState(false);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);

  const jobQuery = useImageZipJobQuery(
    { projectId, dailyLogId, jobId },
    { stopPolling: isCancelling || pollingTimedOut },
  );
  const cancelJobMutation = useCancelImageZipJobMutation();
  const createJobMutation = useCreateImageZipJobMutation();
  const queryClient = useQueryClient();

  const completionFetchedRef = useRef(false);
  const isMountedRef = useRef(true);
  const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (completionTimeoutRef.current !== null) {
        clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
      if (pollingTimeoutRef.current !== null) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  const jobStatus = jobQuery.data?.status;
  const processRate = getImageZipProcessRateNumber(jobQuery.data?.progress_rate ?? null) ?? 0;
  const isJobDone = jobStatus === "COMPLETE" || processRate >= 100;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (pollingTimeoutRef.current !== null) clearTimeout(pollingTimeoutRef.current);
    pollingTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      completionFetchedRef.current = true;
      setPollingTimedOut(true);
      setDialogStatus("error");
      EventLogger.result(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_FAIL, { ...downloadProperties, reason: "polling_timeout" });
      pollingTimeoutRef.current = null;
    }, POLLING_TIMEOUT_MS);

    return () => {
      if (pollingTimeoutRef.current !== null) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [jobId]);

  useEffect(() => {
    if (isJobDone || jobStatus === "FAILED" || jobStatus === "CANCELLED") {
      if (pollingTimeoutRef.current !== null) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    }
  }, [isJobDone, jobStatus]);

  useEffect(() => {
    if (jobStatus === "FAILED") {
      setDialogStatus("error");
      EventLogger.result(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_FAIL, { ...downloadProperties, reason: "job_failed" });
      return;
    }
    if (jobStatus === "CANCELLED") {
      close();
      return;
    }
    if (!isJobDone || completionFetchedRef.current) return;

    completionFetchedRef.current = true;

    const fetchStarted = performance.now();
    queryClient
      .fetchQuery({
        queryKey: downloadImagesQueryKeys.imageZipFile({ projectId, dailyLogId }),
        queryFn: () => downloadImagesApi.getImageZipFile({ projectId, dailyLogId }),
        staleTime: 0,
      })
      .then(async (data) => {
        if (!isMountedRef.current) return;

        const elapsed = performance.now() - fetchStarted;
        const remaining = Math.max(0, COMPLETION_DISPLAY_MS - elapsed);
        if (remaining > 0) {
          await new Promise<void>((resolve) => {
            completionTimeoutRef.current = setTimeout(() => {
              completionTimeoutRef.current = null;
              resolve();
            }, remaining);
          });
        }

        if (!isMountedRef.current) return;

        if (data.status === "READY") {
          setPresignedUrl(data.presigned_url);
          setDialogStatus("success");
        } else {
          setDialogStatus("error");
          EventLogger.result(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_FAIL, { ...downloadProperties, reason: "file_not_ready" });
        }
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setDialogStatus("error");
        EventLogger.result(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_FAIL, { ...downloadProperties, reason: "fetch_failed" });
      });
  }, [isJobDone, jobStatus, projectId, dailyLogId, queryClient, close, downloadProperties]);

  const handleCancel = async () => {
    EventLogger.click(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_CANCEL, downloadProperties);
    setIsCancelling(true);
    try {
      await cancelJobMutation.mutateAsync({ projectId, dailyLogId, jobId });
    } catch {}
    close();
  };

  const handleDownload = () => {
    if (!presignedUrl) return;
    EventLogger.result(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_SUCCESS, { ...downloadProperties, source: "job" });
    triggerDownload(presignedUrl);
    toast.success("Download started. Check Files or Downloads.");
    close();
  };

  const handleRetry = async () => {
    completionFetchedRef.current = false;
    setPresignedUrl(null);
    setDialogStatus("progress");
    setIsCancelling(false);
    setPollingTimedOut(false);

    try {
      const fileData = await queryClient.fetchQuery({
        queryKey: downloadImagesQueryKeys.imageZipFile({ projectId, dailyLogId }),
        queryFn: () => downloadImagesApi.getImageZipFile({ projectId, dailyLogId }),
        staleTime: 0,
      });

      if (!isMountedRef.current) return;

      if (fileData.status === "READY") {
        completionFetchedRef.current = true;
        setPresignedUrl(fileData.presigned_url);
        setDialogStatus("success");
        return;
      }

      const result = await createJobMutation.mutateAsync({ projectId, dailyLogId });
      if (!isMountedRef.current) return;
      setJobId(result.job_id);
    } catch {
      if (!isMountedRef.current) return;
      setDialogStatus("error");
      EventLogger.result(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_FAIL, { ...downloadProperties, reason: "retry_failed" });
    }
  };

  const displayProgress = isJobDone ? 100 : processRate;

  return (
    <DownloadProgressDialog
      open={isOpen}
      status={dialogStatus}
      progress={displayProgress}
      onCancel={handleCancel}
      onDownload={handleDownload}
      onRetry={handleRetry}
      onDone={close}
    />
  );
}

interface DownloadImagesButtonProps {
  projectId: string;
  dailyLogId: string;
  imageCount: number;
}

export function DownloadImagesButton({ projectId, dailyLogId, imageCount }: DownloadImagesButtonProps) {
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);

  const zipFileQuery = useImageZipFileQuery({ projectId, dailyLogId });
  const confirmOverlay = useOverlay();
  const progressOverlay = useOverlay();

  useEffect(() => {
    if (zipFileQuery.data?.status === "READY") {
      setPresignedUrl(zipFileQuery.data.presigned_url);
    }
  }, [zipFileQuery.data]);

  const handleButtonClick = () => {
    const zipStatus = zipFileQuery.data?.status;
    if (zipStatus === "READY" && presignedUrl) {
      EventLogger.result(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_SUCCESS, {
        project_id: projectId,
        log_id: dailyLogId,
        image_count: imageCount,
        source: "cached",
      });
      triggerDownload(presignedUrl);
      toast.success("Download started. Check Files or Downloads.");
      return;
    }

    confirmOverlay.open(({ isOpen, close }) => (
      <DownloadImagesConfirmModal
        isOpen={isOpen}
        close={close}
        imageCount={imageCount}
        projectId={projectId}
        dailyLogId={dailyLogId}
        onJobCreated={(jobId) => {
          progressOverlay.open(({ isOpen, close }) => (
            <DownloadProgressFlow
              isOpen={isOpen}
              close={close}
              projectId={projectId}
              dailyLogId={dailyLogId}
              initialJobId={jobId}
              imageCount={imageCount}
            />
          ));
        }}
      />
    ));
  };

  return (
    <button type="button" onClick={handleButtonClick} aria-label="Download site pictures">
      <span className="inline-block p-2 bg-blue-10 rounded-[4px]">
        <ArrowDownToLine size={16} className="text-blue-500" />
      </span>
    </button>
  );
}
